import React, { useState } from "react";

const WhyUs: React.FC = () => {
  const styles = {
    global: {
      margin: 0,
      padding: 0,
      boxSizing: 'border-box' as const,
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    },
    body: {
      background: 'linear-gradient(135deg, #d4f3ea 0%, #CCFBF1 100%)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
    },
    headerContainer: {
      textAlign: 'center' as const,
      maxWidth: '800px',
      marginBottom: '3rem',
    },
    title: {
      color: '#1f2937',
      fontSize: '2.5rem',
      fontWeight: 700,
      marginBottom: '1rem',
      letterSpacing: '-0.02em',
    },
    titleHighlight: {
      color: '#F59E0B',
    },
    description: {
      color: '#4b5563',
      fontSize: '1.125rem',
      lineHeight: 1.6,
      marginBottom: '0.5rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '2rem',
      maxWidth: '1200px',
      width: '100%',
      margin: '0 auto',
    },
    box: {
      background: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(4px)',
      borderRadius: '30px',
      padding: '2.5rem 2.5rem',
      textAlign: 'center' as const,
      borderBottom: '3px solid #F59E0B',
      transition: '0.3s',
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
      cursor: 'default',
    },
    boxHover: {
      transform: 'translateY(-6px)',
      boxShadow: '0 15px 25px -8px rgba(245, 158, 11, 0.4)',
      background: 'rgba(255, 255, 255, 0.95)',
    },
    icon: {
      fontSize: '3rem',
      color: '#F59E0B',
      marginBottom: '1.5rem',
      display: 'block',
    },
    heading: {
      color: '#1f2937',
      fontSize: '1.5rem',
      fontWeight: 600,
      marginBottom: '0.5rem',
    },
    paragraph: {
      color: '#4b5563',
      fontSize: '1rem',
    },
  };

  // Responsive styles for mobile
  const mobileStyles = `
    @media (max-width: 768px) {
      .why-grid {
        grid-template-columns: 1fr !important;
        gap: 1.5rem !important;
      }
      
      .why-title {
        font-size: 2rem !important;
      }
      
      .why-description {
        font-size: 1rem !important;
        padding: 0 15px !important;
      }
    }
  `;

  const [hoveredBox, setHoveredBox] = useState<number | null>(null);

  return (
    <>
      <style>{mobileStyles}</style>
      <div style={styles.body}>
        {/* Header Section with Title and Description */}
        <div style={styles.headerContainer}>
          <h1 style={styles.title} className="why-title">
            Why <span style={styles.titleHighlight}>Choose Us</span>
          </h1>
          <p style={styles.description} className="why-description">
            With over  years of industry experience, we provide unparalleled 
            quality and service to our valued customers. Our commitment to excellence 
            ensures you get the best equipment, support, and value in the market.
          </p>
          <p style={{...styles.description, fontSize: '1rem', color: '#6b7280'}}>
            Here's what sets us apart from the competition:
          </p>
        </div>

        {/* Features Grid */}
        <div className="why-grid" style={styles.grid}>
          {/* Sales */}
          <div
            style={{
              ...styles.box,
              ...(hoveredBox === 0 ? styles.boxHover : {}),
            }}
            onMouseEnter={() => setHoveredBox(0)}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <i style={styles.icon} className="fas fa-tag"></i>
            <h3 style={styles.heading}>Sales</h3>
            <p style={styles.paragraph}>Market-leading equipment at competitive prices.</p>
          </div>

          {/* Service */}
          <div
            style={{
              ...styles.box,
              ...(hoveredBox === 1 ? styles.boxHover : {}),
            }}
            onMouseEnter={() => setHoveredBox(1)}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <i style={styles.icon} className="fas fa-wrench"></i>
            <h3 style={styles.heading}>Service</h3>
            <p style={styles.paragraph}>Expert support, minimal downtime with 24/7 assistance.</p>
          </div>

          {/* Spares */}
          <div
            style={{
              ...styles.box,
              ...(hoveredBox === 2 ? styles.boxHover : {}),
            }}
            onMouseEnter={() => setHoveredBox(2)}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <i style={styles.icon} className="fas fa-boxes"></i>
            <h3 style={styles.heading}>Spares</h3>
            <p style={styles.paragraph}>Authentic spares in stock with fast delivery.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default WhyUs;